import React, { useEffect, useState } from 'react'
import { Box, Modal, TextHeading, useModalContext } from '@ally/metronome-ui'
import { useHistory } from 'react-router-dom'
import { useCustomizeDashboard } from './CustomizeDashboardContext/CustomizeDashboardContext'
import CustomizeTabs from './CustomizeTabs'
import CustomizeModalTrigger from './CustomizeModalTrigger'

interface ComponentProps {
  lob?: string
  triggerIsIcon?: boolean
}

const CustomizeModalContent: React.FC<{
  headingRef: React.RefObject<HTMLHeadingElement>
  defaultTab: number
}> = ({ headingRef, defaultTab }) => {
  return (
    // Padding can be removed with v23
    <Box p="sm">
      <TextHeading tag="h1" ref={headingRef} tabIndex={-1} size="md">
        Customize
      </TextHeading>
      <CustomizeTabs headingRef={headingRef} defaultTab={defaultTab} />
    </Box>
  )
}

const CustomizeModal: React.FC<ComponentProps> = ({
  lob,
  triggerIsIcon = false,
}): React.ReactElement => {
  const headingRef = React.createRef<HTMLHeadingElement>()
  const [defaultTab, setDefaultTab] = useState<number>(0)
  const [{ isCustomizeModalOpen }, dispatch] = useCustomizeDashboard()
  const history = useHistory()
  const { search } = window.location
  const context = useModalContext()

  useEffect(() => {
    const command = new URLSearchParams(search).get('dashboard-remote-command')
    if (command?.startsWith('open-customize-modal')) {
      history.replace({ search: '' })
      if (command === 'open-customize-modal-Manage') {
        setDefaultTab(1)
      }
      /**
       * This method of receiving commands from the URL is asynchronous and
       * was causing focus issues with the modal if the modal was opened before
       * the Promise resolves. Using setTimeout prevents opening the Modal
       * too soon.
       **/
      setTimeout(() => {
        dispatch({ type: 'set isCustomizeModalOpen', payload: true })
      }, 0)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  return (
    <Modal
      ref={headingRef}
      width="md"
      trigger={
        <CustomizeModalTrigger lob={lob} triggerIsIcon={triggerIsIcon} />
      }
      content={
        <CustomizeModalContent
          headingRef={headingRef}
          defaultTab={defaultTab}
        />
      }
      context={context}
      setShowOption={isCustomizeModalOpen}
      toggleCallback={(isOpening: boolean): void => {
        dispatch({ type: 'set isCustomizeModalOpen', payload: false })
        // if the remote command is used we want to reset the default tab to 0
        setDefaultTab(0)
      }}
      manualToggle
    />
  )
}

export default CustomizeModal
